import { useState, useRef, useCallback, useEffect } from 'react';

interface RolloutSliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export default function RolloutSlider({ value, onChange, disabled }: RolloutSliderProps) {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(e.target.value);
      setLocalValue(newValue);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        onChange(newValue);
      }, 500);
    },
    [onChange],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const barColor =
    localValue === 0
      ? 'bg-gray-300'
      : localValue < 50
        ? 'bg-yellow-400'
        : localValue < 100
          ? 'bg-brand-500'
          : 'bg-green-500';

  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="relative flex-1">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${localValue}%` }}
          />
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={localValue}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
      <span className="text-xs font-medium text-gray-600 w-9 text-right">
        {localValue}%
      </span>
    </div>
  );
}
